import { getChordShape } from '@/lib/chords';

const STRINGS = 6;
const FRETS = 5;

export default function ChordDiagram({ name, size = 100 }) {
  const shape = getChordShape(name);
  if (!shape) {
    return (
      <div className="inline-flex flex-col items-center" style={{ width: size }} data-testid={`chord-${name}`}>
        <div className="w-full aspect-[3/4] rounded-lg border border-dashed border-stone-700 flex items-center justify-center text-stone-500 text-xs px-2 text-center">?</div>
        <div className="text-xs font-medium text-stone-300 mt-1">{name}</div>
      </div>
    );
  }
  const { frets, fingers = [], barre } = shape;

  // Determine starting fret (for chords up the neck)
  const numeric = frets.filter(f => typeof f === 'number' && f > 0);
  const minFret = numeric.length ? Math.min(...numeric) : 0;
  const startFret = minFret > 4 ? minFret : 1;
  const showStartLabel = startFret > 1;

  const w = size;
  const h = size * 1.15;
  const padX = size * 0.15;
  const padTop = size * 0.22;
  const padBottom = size * 0.15;
  const gridW = w - padX * 2;
  const gridH = h - padTop - padBottom;
  const stringGap = gridW / (STRINGS - 1);
  const fretGap = gridH / FRETS;

  const dots = [];
  const marks = [];
  frets.forEach((f, idx) => {
    // idx 0 = string 6 (low E)
    const x = padX + idx * stringGap;
    if (f === 'x') {
      marks.push({ type: 'x', x, y: padTop - size * 0.09 });
    } else if (f === 0) {
      marks.push({ type: 'o', x, y: padTop - size * 0.09 });
    } else {
      const rel = f - startFret + 1;
      if (rel >= 1 && rel <= FRETS) {
        dots.push({ x, y: padTop + fretGap * (rel - 0.5), finger: fingers[idx] });
      }
    }
  });

  // Barre visualization
  let barreShape = null;
  if (barre) {
    const relBarre = barre.fret - startFret + 1;
    if (relBarre >= 1 && relBarre <= FRETS) {
      const xFrom = padX + (STRINGS - barre.from) * stringGap;
      const xTo = padX + (STRINGS - barre.to) * stringGap;
      const y = padTop + fretGap * (relBarre - 0.5);
      barreShape = { x1: Math.min(xFrom, xTo), x2: Math.max(xFrom, xTo), y };
    }
  }

  return (
    <div className="inline-flex flex-col items-center" style={{ width: w }} data-testid={`chord-${name}`}>
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="block">
        {/* Nut or start fret label */}
        {!showStartLabel && (
          <rect x={padX - 1} y={padTop - 3} width={gridW + 2} height={4} fill="#e7e5e4" />
        )}
        {showStartLabel && (
          <text x={padX - 6} y={padTop + fretGap * 0.7} fill="#f97316" fontSize={size * 0.13} textAnchor="end" fontFamily="serif" fontWeight="600">
            {startFret}fr
          </text>
        )}
        {/* Frets */}
        {Array.from({ length: FRETS + 1 }).map((_, i) => (
          <line
            key={`f${i}`}
            x1={padX} x2={padX + gridW}
            y1={padTop + fretGap * i} y2={padTop + fretGap * i}
            stroke="#57534e" strokeWidth={i === 0 && !showStartLabel ? 0 : 1}
          />
        ))}
        {/* Strings */}
        {Array.from({ length: STRINGS }).map((_, i) => (
          <line
            key={`s${i}`}
            x1={padX + i * stringGap} x2={padX + i * stringGap}
            y1={padTop} y2={padTop + gridH}
            stroke="#78716c" strokeWidth={1}
          />
        ))}
        {/* Open / muted markers */}
        {marks.map((m, i) => (
          m.type === 'x' ? (
            <g key={`m${i}`} stroke="#a8a29e" strokeWidth={1.5}>
              <line x1={m.x - 3} y1={m.y - 3} x2={m.x + 3} y2={m.y + 3} />
              <line x1={m.x - 3} y1={m.y + 3} x2={m.x + 3} y2={m.y - 3} />
            </g>
          ) : (
            <circle key={`m${i}`} cx={m.x} cy={m.y} r={size * 0.038} fill="none" stroke="#a8a29e" strokeWidth={1.5} />
          )
        ))}
        {/* Barre */}
        {barreShape && (
          <rect
            x={barreShape.x1 - size * 0.05}
            y={barreShape.y - size * 0.06}
            width={barreShape.x2 - barreShape.x1 + size * 0.1}
            height={size * 0.12}
            rx={size * 0.06}
            fill="#c2410c"
          />
        )}
        {/* Finger dots */}
        {dots.map((d, i) => (
          <g key={`d${i}`}>
            <circle cx={d.x} cy={d.y} r={size * 0.075} fill="#c2410c" />
            {d.finger > 0 && (
              <text x={d.x} y={d.y + size * 0.032} fill="#fff" fontSize={size * 0.09} textAnchor="middle" fontFamily="sans-serif" fontWeight="700">
                {d.finger}
              </text>
            )}
          </g>
        ))}
      </svg>
      <div className="text-sm font-display text-stone-100 -mt-1">{name}</div>
    </div>
  );
}
